/**
 * Component for PaletteBanner
 *
 * @module ui/modules/PaletteBanner
 * @memberof -Common
 */
import './PaletteBanner.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import { BannerCompact as PalBannerCompact } from 'web-palette/dist/components/BannerCompact/BannerCompact';
import { BannerDefault as PalBannerDefault } from 'web-palette/dist/components/BannerDefault/BannerDefault';
import { BannerJumbo as PalBannerJumbo } from 'web-palette/dist/components/BannerJumbo/BannerJumbo';
import { BannerMedia as PalBannerMedia } from 'web-palette/dist/components/BannerMedia/BannerMedia';
import { BannerOversized as PalBannerOversized } from 'web-palette/dist/components/BannerOversized/BannerOversized';
import { actionFormatter } from 'web-palette/dist/util/actionFormatter/actionFormatter';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import { Tooltip } from '@ulta/core/components/Tooltip/Tooltip';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { getButtonHtml } from '@ulta/core/utils/getButtonHtml/getButtonHtml';
import { getExternalAction } from '@ulta/core/utils/getExternalAction/getExternalAction';
import { getLinkHtml } from '@ulta/core/utils/getLinkHtml/getLinkHtml';
import { getUltaAssetHtml } from '@ulta/core/utils/getUltaAssetHtml/getUltaAssetHtml';

import * as utils from './PaletteBanner';

/**
 * Represents a PaletteBanner component
 *
 * @method
 * @param { PaletteBannerProps} props - React properties passed from composition
 * @returns PaletteBanner
 */
export const PaletteBanner = function( props ){
  const { breakpoint } = useDeviceInflection();
  const bannerRef = useRef( null );
  const {
    actionGroups,
    alignToGrid,
    backgroundColor,
    bannerImage,
    body,
    borderColor = defaultProps.borderColor,
    bulletedList,
    className,
    cornerRadius = defaultProps.cornerRadius,
    elevation = defaultProps.elevation,
    eyebrow,
    headline,
    headlineTag = defaultProps.headlineTag,
    headlineColor,
    imagePosition,
    media,
    tag,
    textColor = defaultProps.textColor,
    textPosition = defaultProps.textPosition,
    variant,
    iconName,
    tooltip,
    secondaryLink,
    secondaryTooltip,
    headline2,
    body2,
    headline3,
    body3
  } = props;

  if( variant !== utils.BANNER_VARIANT.MEDIA &&
    variant !== utils.BANNER_VARIANT.DEFAULT &&
    variant !== utils.BANNER_VARIANT.COMPACT &&
    variant !== utils.BANNER_VARIANT.OVERSIZED &&
    variant !== utils.BANNER_VARIANT.JUMBO ){
    return null;
  }

  const { large, medium, small } = media || {};
  const { actions = [], style } = actionGroups || {};

  let bannerContent;

  /** Objects for external media and actions coming from Amplience */
  let formattedMedia;
  let formattedAction;
  let formattedActionFullyClick;

  /** Breakpoint settings for incoming media */
  const isMobile = breakpoint?.isSmallDevice();
  const isDesktop = breakpoint.isLargeDevice();
  const isTablet = breakpoint.isMediumDevice();

  /** Action check for formatters */
  const showActionGroups = actionGroups && actions[0]?.label;
  const hasAction = showActionGroups ;

  /** This checks if there is 1 action and sets the fullyClickableParent to true if so */
  const fullyClickableParent = showActionGroups && actions.length === 1;
  const isKnockoutLink = ( textColor === 'content-knockout' );
  const alignment = ( textPosition === 'center' ) ? 'center' : 'left';

  /** Image check for formatters */
  const hasImage = ( large || small || medium );

  /** Formatters for incoming media and actions */
  if( hasImage ){
    formattedMedia = getUltaAssetHtml( { image: large, mobileImage: small, tabletImage: medium, isMobile: isMobile, isDesktop: isDesktop, isTablet: isTablet } );
  }

  if( hasAction ){
    formattedAction = getExternalAction( { actionGroups: actionGroups, isKnockoutLink: isKnockoutLink, alignment: alignment } );
  }

  if( fullyClickableParent ){
    formattedActionFullyClick = actionFormatter(
      { action: actions[0], style: style, fullyClickableParent: fullyClickableParent },
      { getButtonMarkup: getButtonHtml, getLinkMarkup: getLinkHtml } );
  }

  useIntersectionProcessor( {
    ...props,
    componentRef: bannerRef
  } );

  const formattedBannerImage = bannerImage ? (
    <UltaAsset
      { ...spreadProps( { ...bannerImage } ) }
    />
  ) : undefined;

  switch ( variant ){
    case utils.BANNER_VARIANT.MEDIA:
      bannerContent = (
        <PalBannerMedia
          body={ body }
          bulletedList={ bulletedList }
          externalAction={ formattedAction }
          externalActionFullyClick={ formattedActionFullyClick }
          externalMedia={ formattedMedia }
          eyebrow={ eyebrow }
          fullyClickableParent={ fullyClickableParent }
          headline={ headline }
          headlineTag={ headlineTag }
          tag={ tag }
          textColor={ textColor }
          textPosition={ textPosition }
          alignToGrid={ alignToGrid }
          cornerRadius={ cornerRadius }
          borderColor={ borderColor }
          elevation={ elevation }
        />
      );
      break;
    case utils.BANNER_VARIANT.DEFAULT:
      bannerContent = (
        <PalBannerDefault
          headline={ headline }
          headlineTag={ headlineTag }
          headlineColor={ headlineColor }
          body={ body }
          textColor={ textColor }
          textPosition={ textPosition }
          imagePosition={ imagePosition }
          backgroundColor={ backgroundColor }
          borderColor={ borderColor }
          elevation={ elevation }
          cornerRadius={ cornerRadius }
          alignToGrid={ alignToGrid }
          className={ className }
          externalActionElement={ formattedAction }
          externalImageElement={ formattedBannerImage }
        />
      );
      break;
    case utils.BANNER_VARIANT.COMPACT:
      const tooltipElement = tooltip ?
        <Tooltip { ...spreadProps( tooltip ) } /> :
        undefined;

      const secondaryTooltipElement = secondaryTooltip ?
        <Tooltip { ...spreadProps( secondaryTooltip ) } /> :
        undefined;

      bannerContent = (
        <PalBannerCompact
          headline={ headline }
          headlineTag={ headlineTag }
          iconName={ iconName }
          body={ body }
          textColor={ textColor }
          textPosition={ textPosition }
          backgroundColor={ backgroundColor }
          borderColor={ borderColor }
          tooltip={ tooltipElement }
          secondaryLink={ secondaryLink }
          secondaryTooltip={ secondaryTooltipElement }
          elevation={ elevation }
          cornerRadius={ cornerRadius }
          alignToGrid={ alignToGrid }
          externalActionElement={ formattedAction }
        />
      );
      break;
    case utils.BANNER_VARIANT.OVERSIZED:
      bannerContent = (
        <PalBannerOversized
          iconName={ iconName }
          body={ body }
          textColor={ textColor }
          imagePosition={ imagePosition }
          backgroundColor={ backgroundColor }
          borderColor={ borderColor }
          elevation={ elevation }
          cornerRadius={ cornerRadius }
          alignToGrid={ alignToGrid }
          externalActionElement={ formattedAction }
          externalImageElement={ formattedBannerImage }
          externalFullyClickableActionElement={ formattedActionFullyClick }
          fullyClickableParent={ fullyClickableParent }
        />
      );
      break;
    case utils.BANNER_VARIANT.JUMBO:
      // Temp Production fix for ADA issue, final fix will be done at DXL end.
      const jumboActionGroups = showActionGroups ? actionGroups : undefined;
      bannerContent = (
        <PalBannerJumbo
          tag={ tag }
          eyebrow={ eyebrow }
          headline={ headline }
          headlineTag={ headlineTag }
          body={ body }
          headline2={ headline2 }
          body2={ body2 }
          headline3={ headline3 }
          body3={ body3 }
          bulletedList={ bulletedList }
          textColor={ textColor }
          imagePosition={ imagePosition }
          backgroundColor={ backgroundColor }
          borderColor={ borderColor }
          elevation={ elevation }
          alignToGrid={ alignToGrid }
          externalImageElement={ formattedBannerImage }
          externalActionElement={ formattedAction }
          externalFullyClickableActionElement={ formattedActionFullyClick }
          fullyClickableParent={ fullyClickableParent }
          actionGroups={ jumboActionGroups }
          cornerRadius={ cornerRadius }
        />
      );
      break;
    default:
      break; // default will not happen due to early return and amplience has the variant value as media (default) and its mandatory
  }

  return (
    <div className='PaletteBanner'
      ref={ bannerRef }
    >
      { alignToGrid &&
      <GridContainer>
        { bannerContent }
      </GridContainer>
      }
      { !alignToGrid &&
         bannerContent
      }
    </div>
  );
};

/**
 * Banner Variants
 */
export const BANNER_VARIANT = {
  MEDIA: 'media',
  DEFAULT: 'default',
  COMPACT: 'compact',
  OVERSIZED: 'oversized',
  JUMBO: 'jumbo'
};

/**
 * Banner Min Heights for loading states
 * These are the default values set in component design on figma
 */
export const BANNER_MIN_HEIGHT = {
  [BANNER_VARIANT.MEDIA]: {
    xl: '10.5rem', // 168px
    lg: '10.5rem', // 168px
    md: '10.5rem', // 168px
    sm: '10.5rem' // 168px
  },
  [BANNER_VARIANT.DEFAULT]: {
    xl: '2.5rem', // 40px
    lg: '2.5rem', // 40px
    md: '2.5rem', // 40px
    sm: '3.75rem' // 60px
  },
  [BANNER_VARIANT.COMPACT]: {
    xl: '3.5rem', // 56px
    lg: '3.5rem', // 56px
    md: '3.5rem', // 56px
    sm: '3.5rem' // 56px
  },
  [BANNER_VARIANT.OVERSIZED]: {
    xl: '5.5rem', // 88px
    lg: '5.5rem', // 88px
    md: '4.5rem', // 72px
    sm: '4.5rem' // 72px
  },
  [BANNER_VARIANT.JUMBO]: {
    xl: '18.4375rem', // 294px
    lg: '19.0625rem', // 305px
    md: '30.0625rem', // 481px
    sm: '16.75rem' // 268px
  }
};

/**
 * Property type definitions
 * @typedef PaletteBanner
 * @type {object}
 * @property {'media'|'default'|'compact'|'oversized'|'jumbo'} variant - Sets the Banner variant to render.
 * @property {boolean} [alignToGrid] - Whether to align the banner content to the grid.
 * @property {string} [className] - Optional custom class for the banner container.
 * @property {string} [headline] - Main headline text of the banner.
 * @property {string} [headlineTag] - HTML tag for the headline (e.g., 'h1', 'h2').
 * @property {string} [headlineColor] - Color token for the headline text (Default variant).
 * @property {string} [body] - Body copy / description text of the banner.
 * @property {string} body2 - Sets the body2 text for the banner
 * @property {string} body3 - Sets the body3 text for the banner
 * @property {string} [eyebrow] - Smaller text displayed above the headline (Media variant).
 * @property {array}  [bulletedList] - List of bullet items displayed in the banner body (Media variant).
 * @property {object} [tag] - Tag component displayed above the headline (Media variant).
 * @property {object} [media] - Responsive media object with large, medium, and small images for the Media variant.
 * @property {object} [bannerImage] - Inline image object used for the Default variant.
 * @property {string} [imagePosition] - Position of the inline image relative to text
 * @property {'content-default'|'content-knockout'} [textColor] - Text color style.
 * @property {string} [textPosition] - Text alignment/position token for the banner content.
 * @property {string} [backgroundColor] - Background color token for Default variant.
 * @property {string} [borderColor] - Border color token for Default variant.
 * @property {string} [elevation] - Elevation/shadow style token for Default variant.
 * @property {string} [cornerRadius] - Corner radius token for Default variant.
 * @property {object} [actionGroups] - Action group configuration used to derive CTA actions.
 * @property {string} [iconName] - The name of the icon to display.
 * @property {object} [tooltip] - Tooltip for the primary action.
 * @property {object} [secondaryLink] - An object for rendering a secondary link below the banner.
 * @property {object} [secondaryTooltip] - Tooltip for the secondary action.
 * @property {string} [headline2] - Sets the headline2 text for the banner
 * @property {string} [headline3] - Sets the headline3 text for the banner
 **/

const propTypes = {
  /** Sets the Banner variant to render. **/
  variant: PropTypes.oneOf( ['media', 'default', 'compact', 'oversized', 'jumbo'] ).isRequired,
  /** Whether to align the banner content to the grid. */
  alignToGrid: PropTypes.bool,
  /** Optional custom class for the banner container. */
  className: PropTypes.string,
  /** Main headline text of the banner. */
  headline: PropTypes.string,
  /** HTML tag for the headline (e.g., 'h1', 'h2'). */
  headlineTag: PropTypes.string,
  /** Color token for the headline text (Default variant). */
  headlineColor: PropTypes.string,
  /** Body copy / description text of the banner. */
  body: PropTypes.string,
  /** Smaller text displayed above the headline (Media variant). */
  eyebrow: PropTypes.string,
  /** List of bullet items displayed in the banner body (Media variant). */
  bulletedList: PropTypes.array,
  /** Tag component displayed above the headline (Media variant). */
  tag: PropTypes.object,
  /** Responsive media object with large, medium, and small images */
  media: PropTypes.object,
  /** Inline image object used for the Default variant. */
  bannerImage: PropTypes.object,
  /** Position of the inline image relative to text */
  imagePosition: PropTypes.string,
  /** Text color style. */
  textColor: PropTypes.oneOf( ['content-default', 'content-knockout', 'content-brand-03', 'content-brand-02-strong'] ),
  /** Text alignment/position token for the banner content. */
  textPosition: PropTypes.string,
  /** Background color token for Default variant. */
  backgroundColor: PropTypes.string,
  /** Border color token for Default variant. */
  borderColor: PropTypes.string,
  /** Elevation/shadow style token for Default variant. */
  elevation: PropTypes.string,
  /** Corner radius token for Default variant.*/
  cornerRadius: PropTypes.string,
  /** Action group configuration used to derive CTA actions. */
  actionGroups: PropTypes.object,
  /** The name of the icon to display. */
  iconName:PropTypes.string,
  /** Tooltip for the primary action. */
  tooltip:PropTypes.object,
  /** An object for rendering a secondary link below the banner. */
  secondaryLink:PropTypes.object,
  /** Tooltip for the secondary action. */
  secondaryTooltip:PropTypes.object,
  /** Sets the headline2 text for the banner */
  headline2: PropTypes.string,
  /** Sets the body2 text for the banner */
  body2: PropTypes.string,
  /** Sets the headline3 text for the banner */
  headline3: PropTypes.string,
  /** Sets the body3 text for the banner */
  body3: PropTypes.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} headlineTag - headlineTag: 'h2' - sets the default for the headline tag
 * @property {string} textPosition - textPosition: 'top-left' - sets the default.
 * @property {string} textColor - Text color: 'content-default' - sets the default.
 * @property {string} borderColor - Border color token for Default variant.
 * @property {string} elevation - Elevation/shadow style: 'none' - sets the default.
 * @property {string} cornerRadius - Corner radius: 'border-corners-md' - sets the default.
 */
export const defaultProps =  {
  headlineTag: 'h2',
  textPosition: 'top-left',
  textColor: 'content-default',
  borderColor: 'none',
  elevation: 'none',
  cornerRadius: 'border-corners-md'
};

PaletteBanner.propTypes = propTypes;
PaletteBanner.defaultProps = defaultProps;

PaletteBanner.minHeights = BANNER_MIN_HEIGHT;

export default PaletteBanner;